import { t } from '@lingui/macro';
import { styleConstants } from '@styles/constants';

export type LogsEntityType = 'NotExists' | 'Sites' | 'Account' | 'Devices' | 'Channel' | 'PmZones';

export const logsEntityFn = (): Record<LogsEntityType, string> => ({
  NotExists: t`Not Exists`,
  Sites: t`Sites`,
  Account: t`Account`,
  Devices: t`Devices`,
  Channel: t`Channel`,
  PmZones: t({ message: 'PmZones', id: '[Logs.PmZones]' }),
});

export type LogTableRowState = 'Added' | 'Unchanged' | 'Modified' | 'Deleted';

export const LogTableRowFn = (): Record<LogTableRowState, { text: string; color: string }> => ({
  Added: {
    text: t({ message: 'Added', id: '[LogTable.Added]' }),
    color: styleConstants.colors.green,
  },
  Unchanged: {
    text: t({ message: 'Unchanged', id: '[LogTable.Unchanged]' }),
    color: styleConstants.colors.text,
  },
  Modified: {
    text: t({ message: 'Modified', id: '[LogTable.Modified]' }),
    color: styleConstants.colors.yellow,
  },
  Deleted: {
    text: t({ message: 'Deleted', id: '[LogTable.Deleted]' }),
    color: styleConstants.colors.red,
  },
});

export interface LogTableRowModel {
  values: { [key: string]: string };
  state: LogTableRowState;
}

export interface LogTableColumn {
  name: string;
  type: string;
}

export interface LogTable {
  name?: string;
  columns: LogTableColumn[];
  rows: LogTableRowModel[];
}

export interface LogsModel {
  system?: string;
  timeStamp?: string;
  userId?: number;
  entityId?: number;
  entityType: LogsEntityType;
  username?: string;
  description?: string;
  tables: LogTable[];
}

export interface LogsPagedResponse {
  pageSize: number;
  currentPage: number;
  itemsTotal: number;
  items: LogsModel[];
}

export const ControlSettingsFieldNamesForLogsFn = (): Record<string, string> => ({
  // most values are keyof SettingsFormModel

  // prv / cp control
  prvId: t`PRV Selection`,
  cpId: t`CP Selection`,
  cpChannelId: t`CP Channel Selection`,
  cp2Id: t`CP2 Selection`,
  cp2ChannelId: t`CP2 Channel Selection`,
  cp3Id: t`CP3 Selection`,
  cp3ChannelId: t`CP3 Channel Selection`,

  pressureUnit: t`Pressure unit`,
  flowUnit: t`Flow unit`,

  // note //
  note: t`Note`,

  // mode selection //
  controlMode: t`Control Mode`,
  bigTimeTable: t`Table type`,

  // actuator limits //
  actuatorMax: t`Actuator Settings Maximum`,
  actuatorMin: t`Actuator Settings Minimum`,

  // flow section //
  useZeroFlows: t({ message: 'Zero flows', id: '[InvalidFlows.ZeroFlows]' }),
  faultTimeout: t`Invalid Flows Duration [minutes]`,
  useFlowBandLimits: t`Flow Band Limits`,
  minValidFlow: t`Flow Band Limits Minimum`,
  maxValidFlow: t`Flow Band Limits Maximum`,
  useFaultPressure: t`Invalid Flows Override`,
  invalidFlowOverride: t`Invalid Flows Override`,
  faultPressure: t`Invalid Flows Fixed Outlet value`,

  // overrides //
  holOn: t({
    message: `Use Date Table Override`,
    id: '[ModeSettings.UseDateTableOverride]',
  }),
  holStart: t`Override tables date start`,
  holEnd: t`Override tables date end`,

  // regular time table //
  controlFlags: t`Manual Time Table Control Flag`,

  // fixed outlet //
  cpTarget: t({
    message: 'Target Outlet Pressure',
    id: '[ManualFixedOutlet.TargetOutletPressure]',
  }),

  // cp control //
  cpMin: t`CP Control Target`,
  maxValidCp: t`CP Control Maximum`,
  minValidCp: t`CP Control Minimum`,
  cpBand: t`CP Control Band`,
  // multiple cps (advanced table)
  cpMode: t`CP Control Multiple CPs`,
  cpNightStartMins: t`CP Control between`,
  cpNightEndMins: t`CP Control and`,

  // solenoid //
  solenoidActuationThreshold: t({
    message: 'Solenoid Actuations Threshold',
    id: '[GeneralSettings.Solenoid]',
  }),

  // dead band control //
  deadBand: t({ message: 'Deadband', id: '[Deadband.Deadband]' }),
  deadBandLimit: t`Deadband Control Auto Limit`,

  // single flow table offset //
  nightOffset: t({
    message: 'Offset Pressure',
    id: '[SingleFlowTabOff.OffsetPressure]',
  }),
  nightStartMins: t`Single Pressure Table Offset Between Time`,
  nightEndMins: t`Single Pressure Table Offset And Time`,

  // latch mode //
  latchMode: t({
    message: 'Latch mode',
    id: '[LatchMode.LatchMode]',
  }),

  // controller alarms //
  minInletPres: t`Controller Alarms Inlet Pressure Lower`,
  maxInletPres: t`Controller Alarms Inlet Pressure Upper`,
  minFlow: t`Controller Alarms Flow Lower`,
  maxFlow: t`Controller Alarms Flow Upper`,
  minOutletPres: t`Controller Alarms Outlet Pressure Lower`,
  maxOutletPres: t`Controller Alarms Outlet Pressure Upper`,
  minTopPres: t`Controller Alarms Top Pressure Lower`,
  maxTopPres: t`Controller Alarms Top Pressure Upper`,

  // summer time offset //
  summerStart: t`Summer Time Offset Adjust by`,
  summerEnd: t`Summer Time Offset From Sunday After`,
  summerOffset: t`Summer Time Offset To Sunday After`,

  // learning //
  learningDataLimit: t`Learning Data Limit [%]`,
  learningStatus: t`Learning Status`,

  //
  nextUpdateTime: t`Next Update Time`,
});

export const mapKeysToLowerCase = (data: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(data)?.map(([key, val]) => [key?.toLowerCase(), val]));
